import type { Entity } from '../components'
import type { GameWorld } from '../world'
import type { EventBus } from '../events'
import { applyPlayerRadius } from '../growth'
import { spawnPigmentPuff } from './particles'
import { holdEyesClosed } from './eyes'
import { currentTier } from '../evolution'
import { T } from '../tunables'

// Phase 9 — ticks the player's tier-morph timer, re-applies tier-driven
// visuals each frame so the radius lerps in step with the shader, and
// emits the morph's beats on the event bus:
//   evolution.started   — first tick of a fresh morph (inhale/gather begins)
//   evolution.burst     — progress crosses the burst frame (peak overshoot)
//   evolution.completed — morph timer expires (body settled at new tier)
// Audio/camera/time-scale reactions subscribe in main; the narrator line is
// triggered by the shop at purchase time (milestone tier).
//
// Burst-frame world reactions live here (game-state mutation belongs in a
// system): the pigment cloud, and a radial shove of nearby swimmers so the
// world visibly answers the pulse — overlap extended beyond the body.
export function evolutionSystem(world: GameWorld, player: Entity, dt: number, events: EventBus): void {
  if (!player.evolution) return
  if (player.evolution.morphTimer <= 0) return

  if (!player.evolution.startAnnounced) {
    player.evolution.startAnnounced = true
    events.emit({ type: 'evolution.started', tier: currentTier(player) })
    // Anticipation: eyes ease shut through the gather and snap open at the
    // burst. Derived from the morph's own timeline (not wall-clock) so it
    // stays in sync however morphDuration is tuned; both timers tick the
    // same scaled dt, so slowmo can't desync them either.
    holdEyesClosed(player, T.evolution.morphDuration * T.evolution.burstEnd)
  }

  player.evolution.morphTimer = Math.max(0, player.evolution.morphTimer - dt)
  applyPlayerRadius(player)

  // Burst-frame secondary action — emit once per morph when progress
  // crosses the threshold. Reuses the Phase 7b particle pipeline.
  const u = 1 - player.evolution.morphTimer / T.evolution.morphDuration
  if (!player.evolution.burstPuffed && u >= T.evolution.burstEnd) {
    const x = player.position.x
    const y = player.position.y
    const r = player.renderable?.radius ?? 0.16
    spawnPigmentPuff(world, x, y, [0.55, 0.78, 0.92], r * 1.4)  // pale teal (player base)
    spawnPigmentPuff(world, x, y, [0.78, 0.65, 0.90], r * 1.2)  // dusty lavender
    spawnPigmentPuff(world, x, y, [0.85, 0.40, 0.35], r * 0.9)  // faint tier-2 red
    player.evolution.burstPuffed = true

    // Radial shove — nearby swimmers are pushed by the pulse. NPCs are
    // exempt: Anagenesis happens beside the mother now (proximity-gated
    // Home), and the shove was blasting her out of the hub.
    const range = T.feel.evolve.burstPushRange
    for (const e of world.with('velocity', 'position')) {
      if (e === player || e.npc) continue
      const dx = e.position.x - x
      const dy = e.position.y - y
      const d = Math.hypot(dx, dy)
      if (d <= 0.0001 || d >= range) continue
      const push = T.feel.evolve.burstPushImpulse * (1 - d / range)
      e.velocity.x += (dx / d) * push
      e.velocity.y += (dy / d) * push
    }

    events.emit({ type: 'evolution.burst', tier: currentTier(player) })
  }

  if (player.evolution.morphTimer <= 0) {
    events.emit({ type: 'evolution.completed', tier: currentTier(player) })
  }
}
